import { Color3 } from "@babylonjs/core/Maths/math.color";
import { CreateLineSystem } from "@babylonjs/core/Meshes/Builders/linesBuilder";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { ElementGeometry } from "./ElementGeometry";
import { Util } from "../../utils/Util";
import { PropertyValue, LengthProperty, PropertyChangedResult, StringProperty } from "@egr/wcf/modules/core/prop";
import { clamp } from "@egr/wcf/modules/utils/math";
import { ShadowGenerator } from "@egr/wcf/modules/core/rendering/ShadowGenerator";
import { ElementChangedFlags } from '@egr/wcf/modules/core/mdl';
export class Grid extends ElementGeometry {
    constructor(pRoomManager, pElement) {
        super(pRoomManager, pElement);
        this.mRoomElement = pElement;
        this.mRoomManager = pRoomManager;
        this.mPoints = new Array();
        this.mWidth = 0;
        this.mDepth = 0;
        this.mArea = 0;
        this.mCenter = Vector3.Zero();
        this.mminPoint = Vector3.Zero();
        this.mmaxPoint = Vector3.Zero();
        this.mRoomElement.Params.ElementPoints = new Array();
        this.mRoomElement.Params.ElementOriginalPoints = new Array();
        this.mRoomElement.Params.material = this.mRoomElement.material;
        this.mRoomElement.Params.materialColor = this.getInitialParams().color;
    }
    getType() {
        return ("Grid");
    }
    setMesh(pMesh) {
        this.mArea = Util.calcMeshArea(pMesh) * 0.25;
    }
    get drawnMesh2d() {
        return (this.mMesh2d);
    }
    get drawnMesh3d() {
        return (this.mMesh3d);
    }
    getPoints() {
        return this.mPoints;
    }
    getPolygon(pOffset = true) {
        let tRes = new Array();
        let tnodePos = new Vector3(0, 0, 0);
        if (pOffset)
            tnodePos = this.mRoomElement.position.clone();
        tRes.push(this.mPoints[0].add(tnodePos));
        tRes.push(this.mPoints[1].add(tnodePos));
        tRes.push(this.mPoints[2].add(tnodePos));
        tRes.push(this.mPoints[3].add(tnodePos));
        return (tRes);
    }
    get width() {
        return (this.mWidth);
    }
    get depth() {
        return this.mDepth;
    }
    center() {
        return this.mCenter;
    }
    get minPoint() {
        return (this.mminPoint);
    }
    get maxPoint() {
        return (this.mmaxPoint);
    }
    showInteractors(pShow) {
        if (this.mInteractors != null)
            this.mInteractors.showInteractors(pShow);
    }
    correctDirection() {
        let tClockwise = Util._clockWise3(this.mPoints);
        if (tClockwise)
            this.mPoints.reverse();
    }
    checkPointOrder() {
        let tmaxX = -Infinity;
        let tminY = Infinity;
        for (let tPoint of this.mPoints) {
            if (tPoint.x > tmaxX)
                tmaxX = tPoint.x;
            if (tPoint.z < tminY)
                tminY = tPoint.z;
        }
        if (Util.numbersEqual(this.mPoints[0].x, tmaxX, 0.01) &&
            Util.numbersEqual(this.mPoints[0].z, tminY, 0.01)) {
            let tPoints = [];
            for (let tPoint of this.mPoints) {
                tPoints.push(tPoint.clone());
            }
            this.mPoints = [];
            this.mPoints.push(tPoints[2]);
            this.mPoints.push(tPoints[3]);
            this.mPoints.push(tPoints[0]);
            this.mPoints.push(tPoints[1]);
        }
    }
    getInitialParams() {
        let tParams = {};
        tParams.width = this.mRoomElement.gridCellSize.x;
        tParams.depth = this.mRoomElement.gridCellSize.y;
        tParams.height = 0;
        tParams.floorOffset = 0.0;
        tParams.color = "#ff0000";
        return (tParams);
    }
    get Area() {
        return (this.mArea);
    }
    get Perimeter() {
        return (this.mPerimeter);
    }
    getparent() {
        return (this.mParent);
    }
    setNewWidth(pNewWidth) {
        let tLength = clamp(pNewWidth, 0.1, 500);
        let tREPoints = this.getPoints();
        let tsideLine = tREPoints[3].subtract(tREPoints[0]);
        tsideLine.normalize();
        tsideLine = tsideLine.scale(tLength);
        tREPoints[2] = tREPoints[1].add(tsideLine);
        tREPoints[3] = tREPoints[0].add(tsideLine);
        let tREnewPoints = new Array();
        tREnewPoints.push(tREPoints[0]);
        tREnewPoints.push(tREPoints[1]);
        tREnewPoints.push(tREPoints[2]);
        tREnewPoints.push(tREPoints[3]);
        this.setPoints(tREnewPoints);
    }
    setNewDepth(pNewDepth) {
        let tDepth = clamp(pNewDepth, 0.1, 500);
        let tREPoints = this.getPoints();
        let ttopLine = tREPoints[1].subtract(tREPoints[0]);
        ttopLine.normalize();
        ttopLine = ttopLine.scale(tDepth);
        tREPoints[1] = tREPoints[0].add(ttopLine);
        tREPoints[2] = tREPoints[3].add(ttopLine);
        let tREnewPoints = new Array();
        tREnewPoints.push(tREPoints[0]);
        tREnewPoints.push(tREPoints[1]);
        tREnewPoints.push(tREPoints[2]);
        tREnewPoints.push(tREPoints[3]);
        this.setPoints(tREnewPoints);
    }
    create2dGeometry(pParent) {
        this.mParent = pParent;
        this.calcSize();
        let tGrid = this.createGrid(pParent);
        if (tGrid != null) {
            this.mRoomElement.setMesh2d(tGrid);
            this.mRoomElement.Params.MeshVertices = tGrid.getVerticesData(VertexBuffer.PositionKind);
            this.calcLenght();
        }
    }
    create3dGeometry(pParent) {
        // let tRectangle = this.createGrid(pParent, false);
        // if (tRectangle != null) {
        //     this.mRoomElement.setMesh3d(tRectangle);
        // }
    }
    getProperties() {
        let tProperties = new Array();
        let tgridFieldDim = new Array();
        for (let tdim of this.mRoomManager.gridFieldDim) {
            const tdimStr = Util.convertVector2ToDimStr(tdim);
            const tPropValue = new PropertyValue(tdimStr, tdimStr);
            tgridFieldDim.push(tPropValue);
        }
        // grid field dim
        const tgridCellSize = this.mRoomElement.gridCellSize;
        const tgridCellSizeStr = Util.convertVector2ToDimStr(tgridCellSize);
        let tgridCellSizeProp = new StringProperty(this.mRoomElement, Grid.PROP_KEY_GRIDCELLSIZE, 'Grid cell size');
        tgridCellSizeProp.class = "base";
        tgridCellSizeProp.choiceList = true;
        tgridCellSizeProp.getChoices = () => Promise.resolve(tgridFieldDim);
        tgridCellSizeProp.getValue = () => new PropertyValue(tgridCellSizeStr);
        tgridCellSizeProp.setValue = (pValue) => {
            let tValue = StringProperty.ConvertValueToString(pValue);
            if (tValue != null) {
                const tgridSize = this.mRoomManager._getGridSize(tValue);
                if (tgridSize != null) {
                    this.mRoomElement.setGridCellSize(tgridSize);
                    this.mRoomElement.notifyChange(ElementChangedFlags.Properties);
                    this.mRoomElement.notifyChange(ElementChangedFlags.PropertyValues);
                    return (Promise.resolve(PropertyChangedResult.Value));
                }
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tgridCellSizeProp);
        //width
        let tWidthProp = new LengthProperty(this.mRoomElement, Grid.PROP_KEY_WIDTH, this.mRoomManager.resources.propertyLength);
        tWidthProp.class = "base";
        tWidthProp.editable = false;
        tWidthProp.getValue = () => new PropertyValue(this.mRoomElement.width);
        tWidthProp.setValue = (pValue) => {
            let tValue = LengthProperty.ConvertValueToNumber(pValue);
            if (tValue != null && this.mRoomElement.setWidth(tValue)) {
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tWidthProp);
        //depth
        let tDepthProp = new LengthProperty(this.mRoomElement, Grid.PROP_KEY_DEPTH, this.mRoomManager.resources.propertyWidth);
        tDepthProp.class = "base";
        tDepthProp.editable = false;
        tDepthProp.getValue = () => new PropertyValue(this.mRoomElement.depth);
        tDepthProp.setValue = (pValue) => {
            let tValue = LengthProperty.ConvertValueToNumber(pValue);
            if (tValue != null && this.mRoomElement.setDepth(tValue)) {
                return (Promise.resolve(PropertyChangedResult.Value));
            }
            return (Promise.resolve(PropertyChangedResult.Nothing));
        };
        tProperties.push(tDepthProp);
        return (tProperties);
    }
    serializeIOData() {
        return (this.mRoomElement.Params);
    }
    loadIOData(pData) {
        let tParams = pData;
        let tPoints = new Array();
        for (let tEP of tParams.ElementPoints) {
            let tP = new Vector3(0, 0, 0);
            tP.x = tEP.x;
            tP.z = tEP.z;
            tPoints.push(tP);
        }
        this.setPoints(tPoints);
    }
    setPoints(pPoints, pCreateCAG = false) {
        const tsnappedPoints = this.calcsnappedGrid(pPoints);
        let tdistX = Vector3.Distance(tsnappedPoints[0], tsnappedPoints[3]);
        let tdistZ = Vector3.Distance(tsnappedPoints[0], tsnappedPoints[1]);
        if (tdistX < 0.09 || tdistZ < 0.091) {
            return (false);
        }
        this.mPoints = [];
        let tP1 = tsnappedPoints[0];
        let tP2 = tsnappedPoints[1];
        let tP3 = tsnappedPoints[2];
        let tP4 = tsnappedPoints[3];
        this.mPoints.push(tP1);
        this.mPoints.push(tP2);
        this.mPoints.push(tP3);
        this.mPoints.push(tP4);
        this.correctDirection();
        this.mRoomElement.Params.ElementPoints = this.mPoints;
        this.calcSize();
        this.mRoomElement.setClosedPolygon(true);
        if (pCreateCAG) {
            let tPoints = new Array();
            // tslint:disable-next-line:prefer-for-of
            for (let i = 0; i < this.mPoints.length; i++) {
                tPoints.push(new CSG.Vector2D(this.mPoints[i].x, this.mPoints[i].z));
            }
            let tCAG = CAG.fromPointsNoCheck(tPoints);
            this.mRoomElement.setCAG(tCAG);
            return (true);
        }
        return (false);
    }
    calcLenght() {
        let tLines = new Array();
        let tP1 = this.mRoomElement.Params.ElementPoints[0];
        let tP2 = this.mRoomElement.Params.ElementPoints[1];
        let tP3 = this.mRoomElement.Params.ElementPoints[2];
        let tP4 = this.mRoomElement.Params.ElementPoints[3];
        tLines.push([tP1, tP2]);
        tLines.push([tP1, tP4]);
        tLines.push([tP4, tP3]);
        tLines.push([tP3, tP2]);
        let tDist = 0;
        for (let tLine of tLines) {
            tDist += Vector3.Distance(tLine[0], tLine[1]);
        }
        this.mPerimeter = tDist;
        this.calcArea(tLines);
    }
    calcArea(pLines) {
        let tSide1 = Vector3.Distance(pLines[0][0], pLines[0][1]);
        let tSide2 = Vector3.Distance(pLines[1][0], pLines[1][1]);
        let tarea = tSide1 * tSide2;
        this.mArea = tarea;
    }
    calcSize() {
        if (this.mPoints.length === 0)
            return;
        let tminX = this.mPoints[0].x;
        let tminY = this.mPoints[0].z;
        let tmaxX = this.mPoints[0].x;
        let tmaxY = this.mPoints[0].z;
        for (let tPoint of this.mPoints) {
            tminX = Math.min(tPoint.x, tminX);
            tmaxX = Math.max(tPoint.x, tmaxX);
            tminY = Math.min(tPoint.z, tminY);
            tmaxY = Math.max(tPoint.z, tmaxY);
        }
        this.mWidth = Vector3.Distance(this.mRoomElement.Params.ElementPoints[0], this.mRoomElement.Params.ElementPoints[3]);
        this.mDepth = Vector3.Distance(this.mRoomElement.Params.ElementPoints[0], this.mRoomElement.Params.ElementPoints[1]);
        this.mRoomElement.setWidth(this.mWidth, false, false);
        this.mRoomElement.setDepth(this.mDepth, false, false);
        this.mminPoint = new Vector3(tminX, 0, tminY);
        this.mmaxPoint = new Vector3(tmaxX, 0, tmaxY);
        this.mCenter.x = tmaxX - (tmaxX - tminX) * 0.5;
        this.mCenter.z = tmaxY - (tmaxY - tminY) * 0.5;
    }
    calcsnappedGrid(pPoints) {
        const trX = this.mRoomElement.gridCellSize.x;
        const trZ = this.mRoomElement.gridCellSize.y;
        const tstartP = pPoints[0].clone();
        const tdirX = (pPoints[3].subtract(pPoints[0])).normalize();
        const tdirZ = (pPoints[1].subtract(pPoints[0])).normalize();
        const tdistX = clamp(Math.abs(pPoints[2].x - tstartP.x), trX, 1000);
        const tdistZ = clamp(Math.abs(pPoints[2].z - tstartP.z), trZ, 1000);
        const tW = trX * Math.round(tdistX / trX);
        const tL = trZ * Math.round(tdistZ / trZ);
        const tnewX = pPoints[0].add(tdirX.scale(tW));
        const tnewZ = pPoints[0].add(tdirZ.scale(tL));
        const tP1 = new Vector3(pPoints[0].x, pPoints[1].y, tnewZ.z);
        const tP2 = new Vector3(tnewX.x, pPoints[2].y, tnewZ.z);
        const tP3 = new Vector3(tnewX.x, pPoints[3].y, pPoints[3].z);
        const tRes = [];
        tRes.push(pPoints[0], tP1, tP2, tP3);
        return (tRes);
    }
    /*
    *     tP0--------------------------tP3
    *      |                            |
    *      |                            |
    *      |                            |
    *     tP1--------------------------tP2
    */
    createGrid(pParent, p2d = true) {
        if (this.mPoints.length === 0) {
            return (null);
        }
        const trX = this.mRoomElement.gridCellSize.x;
        const trZ = this.mRoomElement.gridCellSize.y;
        let tXstart = this.mPoints[0].x;
        let tXend = this.mPoints[3].x;
        let tZstart = this.mPoints[0].z;
        let tZend = this.mPoints[1].z;
        let tColor = Color3.FromHexString('#0000ff');
        let tColor1Lines = new Array();
        //trX meter steps
        for (let i = tXstart; i <= tXend; i += trX) {
            tColor1Lines.push([
                new Vector3(i, 0, tZstart),
                new Vector3(i, 0, tZend)
            ]);
        }
        //trZ meter steps
        for (let i = tZstart; i >= tZend; i -= trZ) {
            tColor1Lines.push([
                new Vector3(tXstart, 0, i),
                new Vector3(tXend, 0, i)
            ]);
        }
        let tLineMesh = CreateLineSystem("gridLines", { lines: tColor1Lines, updatable: false, instance: null }, this.mScene);
        tLineMesh.color = tColor;
        // tLineMesh.alpha = tAlpha1;
        tLineMesh.intersectionThreshold = 0.15;
        tLineMesh.renderingGroupId = 2;
        let tpickable = false;
        if (this.mRoomElement.group2d != null) {
            tpickable = this.mRoomElement.group2d.isEnabled();
        }
        tLineMesh.isPickable = tpickable;
        tLineMesh.parent = pParent;
        tLineMesh.freezeWorldMatrix();
        if (!p2d) {
            tLineMesh.visibility = 0;
        }
        ShadowGenerator.disableMeshShadow(tLineMesh);
        this.prepareMesh(tLineMesh, p2d);
        // this.createPickPlane(pParent, p2d);
        return (tLineMesh);
    }
    createPickPlane(pParent, p2d = true) {
        const tW = this.mRoomElement.width;
        const tD = this.mRoomElement.depth;
        //invisible geometry for picking
        let tSelectionPlane = CreatePlane("gridPickPlane", {
            width: tW,
            height: tD
        }, this.mScene);
        tSelectionPlane.position.y = 0;
        tSelectionPlane.rotation.x = -Math.PI / 2;
        tSelectionPlane.parent = pParent;
        tSelectionPlane.visibility = 0;
        ShadowGenerator.disableMeshShadow(tSelectionPlane);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
Grid.PROP_KEY_WIDTH = "room:RoomElement:Grid:width";
Grid.PROP_KEY_DEPTH = "room:RoomElement:Grid:depth";
Grid.PROP_KEY_GRIDCELLSIZE = "room:RoomElement:Grid:gridCellSize";
